import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Combobox, Checkbox, TextInput, TextArea, Option } from '@code/innovance-react-ui-kit';

import {
  FormRow,
  FormElement,
  CheckboxContainer,
  TextAreaContainer,
  Title,
} from 'src/front/components/FormStyles';

import { PtsSearchResponseData, ReferenceDataType } from 'src/front/api/generated';

import { convertStringToDate } from 'src/front/common/utils/dateTimeUtils';
import { optionFormat } from 'src/front/common/utils/formUtils';
import usePRM from 'src/front/common/hooks/usePRM';

import Datepicker from 'src/front/components//Datepicker';

interface Props {
  selectedRecordData: PtsSearchResponseData;
  sendType: Option | null;
  setSendType: Dispatch<SetStateAction<Option | null>>;
  setIsUpdateDisabled: Dispatch<SetStateAction<boolean>>;
}

const SelectedRecordData: FC<Props> = ({
  selectedRecordData,
  sendType,
  setSendType,
  setIsUpdateDisabled,
}) => {
  const { t } = useTranslation();

  const poolPRM = usePRM(ReferenceDataType.PTSPOOL);
  const statusPRM = usePRM(ReferenceDataType.PTSSTATUS);
  const [status, setStatus] = useState<Option | null>(null);
  const [disableSendType, setDisableSendType] = useState<boolean>(true);

  useEffect(() => {
    if (selectedRecordData.status.code === '1') setDisableSendType(false);

    if (selectedRecordData.status.code === '2')
      setStatus(statusPRM?.filter((sta) => sta.value === '4')[0]);
    else setStatus(optionFormat(selectedRecordData.status));

    if (selectedRecordData.status.code === '3') setIsUpdateDisabled(true);

    return () => {
      setSendType(null);
      setDisableSendType(true);
      setIsUpdateDisabled(false);
    };
  }, [selectedRecordData, setIsUpdateDisabled, setSendType, statusPRM]);

  const handleSelectSendType = (value: Option | null): void => {
    setSendType(value);
  };

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const disabledField = (): void => {};

  return (
    <>
      <Title>
        <Trans i18nKey="problemTrackingSystem_dataAboutSelectedRecord_text" />
      </Title>
      <FormRow>
        <FormElement>
          <Combobox
            items={[optionFormat(selectedRecordData.levelZero)]}
            label={t('problemTrackingSystem_level0_label')}
            name="PRM_PTS_ZERO_LEVEL"
            onChange={disabledField}
            value={optionFormat(selectedRecordData.levelZero)}
            disabled
          />
        </FormElement>
        <FormElement>
          <Combobox
            items={[optionFormat(selectedRecordData.levelOne)]}
            label={t('problemTrackingSystem_level1_label')}
            name="PRM_MAIN_PROBLEM_TREE"
            onChange={disabledField}
            value={optionFormat(selectedRecordData.levelOne)}
            disabled
          />
        </FormElement>
      </FormRow>
      <FormRow>
        <FormElement>
          <Combobox
            items={[optionFormat(selectedRecordData.levelTwo)]}
            label={t('problemTrackingSystem_level2_label')}
            name="PRM_PTS_LEVEL_TWO"
            onChange={disabledField}
            value={optionFormat(selectedRecordData.levelTwo)}
            disabled
          />
        </FormElement>
        <FormElement>
          <Combobox
            items={[optionFormat(selectedRecordData.levelThree)]}
            label={t('problemTrackingSystem_level3_label')}
            name="PRM_PTS_LEVEL_THREE"
            onChange={disabledField}
            value={optionFormat(selectedRecordData.levelThree)}
            disabled
          />
        </FormElement>
      </FormRow>
      <FormRow>
        <FormElement>
          <Combobox
            items={[optionFormat(selectedRecordData.productType)]}
            label={t('general_components_productType_label')}
            name="PRM_PRODUCTS_PROBLEM_TREE"
            onChange={disabledField}
            value={optionFormat(selectedRecordData.productType)}
            disabled
          />
        </FormElement>
        <FormElement>
          <Combobox
            items={[optionFormat(selectedRecordData.channel)]}
            label={t('general_components_channelCode_label')}
            name="PRM_CHANNEL_PROBLEM_TREE"
            onChange={disabledField}
            value={optionFormat(selectedRecordData.channel)}
            disabled
          />
        </FormElement>
      </FormRow>
      <FormRow>
        <FormElement>
          <Datepicker
            inputLabel={`${t('general_components_deadline_label')}`}
            onChange={disabledField}
            value={convertStringToDate(selectedRecordData.deadline)}
            disabled
          />
        </FormElement>
        <FormElement>
          <CheckboxContainer>
            <Checkbox
              id="SMS"
              label={t('problemTrackingSystem_sms_text')}
              onChange={disabledField}
              value={selectedRecordData.sendSms}
              disabled
            />
          </CheckboxContainer>
          <CheckboxContainer>
            <Checkbox
              id="VIP"
              label={t('problemTrackingSystem_vip_text')}
              onChange={disabledField}
              value={selectedRecordData.vip}
              disabled
            />
          </CheckboxContainer>
        </FormElement>
      </FormRow>
      <FormRow>
        <FormElement>
          <TextInput
            label={t('problemTrackingSystem_classificationCode_label')}
            name="CLASSIFICATION_CODE"
            onChange={disabledField}
            value={selectedRecordData.classificationCode}
            disabled
          />
        </FormElement>
        <FormElement>
          <Combobox
            disabled={disableSendType}
            items={poolPRM}
            label={t('problemTrackingSystem_send_label')}
            name="PRM_PTS_SEND_TYPE"
            onChange={handleSelectSendType}
            value={sendType}
          />
        </FormElement>
      </FormRow>
      <FormRow>
        <FormElement>
          <Combobox
            items={statusPRM}
            label={t('general_status_label')}
            name="PRM_PTS_STATUS_TYPE"
            onChange={disabledField}
            value={status}
            disabled
          />
        </FormElement>
        <FormElement>
          <Combobox
            items={[optionFormat(selectedRecordData.typeOfClosure)]}
            label={t('problemTrackingSystem_typeOfClosure_label')}
            name="PRM_PTS_CLOSURE_TYPE"
            onChange={disabledField}
            value={optionFormat(selectedRecordData.typeOfClosure)}
            disabled
          />
        </FormElement>
      </FormRow>
      <FormRow>
        <TextAreaContainer>
          <TextArea
            label={`${t('problemTrackingSystem_recordDetail_label')}`}
            maxLength={1250}
            onChange={disabledField}
            value={selectedRecordData.problemText}
            disabled
          />
        </TextAreaContainer>
      </FormRow>
    </>
  );
};

export default SelectedRecordData;
